import { NgTemplateOutlet } from "@angular/common";
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { TooltipModule } from "primeng/tooltip";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { TerminologyUsageDeclaration } from "@app/shared/terminology/terminology-usage";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import {
  PlatformAccessAdminDisplayError,
  PlatformRoleCatalogItem,
  PlatformUserCreateRequest,
  PlatformUserCreated,
  mapPlatformAccessAdminError,
} from "./platform-access-admin.models";
import { PlatformAccessAdminService } from "./platform-access-admin.service";
import { PLATFORM_ACCESS_TERMINOLOGY } from "./platform-access.terminology";

type UserCreateDetailField = "displayName" | "email" | "phoneNumber" | "jobTitle" | "department";
type UserCreatePasswordField = "password" | "confirmPassword";
type UserCreateTextFieldName = UserCreateDetailField | UserCreatePasswordField;
type UserCreateFieldName = UserCreateTextFieldName | "roleCodes";
type UserCreateTextKey = keyof typeof PLATFORM_ACCESS_TERMINOLOGY.userCreatePanel;

/**
 * The fixed part of one text field of the new user form. `name` is the reactive-form control and
 * also the property the API reports validation problems under, so server messages land beside the
 * same field. `labelKey` and `hintKeys` name the terminology usages that carry the wording: one
 * hint renders as a sentence beneath the field, several render as a list of rules.
 */
interface PlatformUserCreateFieldSpec {
  name: UserCreateTextFieldName;
  required: boolean;
  type: "text" | "email" | "tel" | "password";
  autocomplete: string;
  labelKey: UserCreateTextKey;
  hintKeys?: UserCreateTextKey[];
}

/** One line of standing help beneath a field, resolved through the vocabulary. */
export interface PlatformUserCreateFieldHint {
  text: string;
  usage: TerminologyUsageDeclaration;
}

/** One text field of the new user form with its label and hints resolved through the vocabulary. */
export interface PlatformUserCreateFieldDefinition extends PlatformUserCreateFieldSpec {
  label: string;
  labelUsage: TerminologyUsageDeclaration;
  hints: PlatformUserCreateFieldHint[];
}

/**
 * Mirrors the limits PlatformUserCreateRequestValidator enforces on the API for the detail
 * fields. The password has no entry: the sign-in policy the API applies (InternalPasswordPolicy)
 * only requires the password to be present and not only whitespace, and it is used exactly as
 * typed, so the form puts no length on it either.
 */
const USER_CREATE_MAX_LENGTHS: Record<UserCreateDetailField, number> = {
  displayName: 200,
  email: 320,
  phoneNumber: 40,
  jobTitle: 150,
  department: 150,
};

const DETAIL_FIELDS: PlatformUserCreateFieldSpec[] = [
  { name: "displayName", required: true, type: "text", autocomplete: "name", labelKey: "displayName" },
  { name: "email", required: true, type: "email", autocomplete: "email", labelKey: "email", hintKeys: ["emailHint"] },
  { name: "phoneNumber", required: false, type: "tel", autocomplete: "tel", labelKey: "phone" },
  { name: "jobTitle", required: false, type: "text", autocomplete: "organization-title", labelKey: "jobTitle" },
  { name: "department", required: false, type: "text", autocomplete: "organization", labelKey: "department" },
];

/**
 * The password the user signs in with and its confirmation. Only the password is sent; the
 * confirmation exists so a typo is caught before the request leaves the browser. The rules
 * listed beneath the password are the whole sign-in policy (InternalPasswordPolicy on the API):
 * required and not only spaces, any length with no particular characters, used exactly as typed.
 */
const PASSWORD_FIELDS: PlatformUserCreateFieldSpec[] = [
  {
    name: "password",
    required: true,
    type: "password",
    autocomplete: "new-password",
    labelKey: "password",
    hintKeys: ["passwordRuleRequired", "passwordRuleLength", "passwordHint"],
  },
  {
    name: "confirmPassword",
    required: true,
    type: "password",
    autocomplete: "new-password",
    labelKey: "confirmPassword",
  },
];

/** Every control in visual order, which is also the order the first invalid one is focused in. */
const FIELD_ORDER: UserCreateFieldName[] = [
  ...DETAIL_FIELDS.map((field) => field.name),
  "roleCodes",
  ...PASSWORD_FIELDS.map((field) => field.name),
];

const ROLE_GROUP_ID = "user-create-roleCodes";
const ROLE_LEGEND_ID = "user-create-roles-title";
const ROLE_HINT_ID = "user-create-hint-roleCodes";
const ROLE_ERROR_ID = "user-create-error-roleCodes";
const ROLE_FILTER_ID = "user-create-role-filter";

/**
 * # New user form
 *
 * The form the Access Management page shows in its detail panel, in place of the selected user,
 * to create a platform user: the person's details, the roles they start with and the password
 * they sign in with. Save and Cancel sit in the header, in the top right corner of the panel.
 *
 * The roles section repeats the Roles tab of a selected user: a filter box, an "n of m selected"
 * count and one checkbox tile per role of the catalog (`roles`), each with the role's name and
 * an info button whose tooltip gives the code and description. Every catalog role can be ticked
 * and any number of them at once; at least one is required. The tiles keep their alphabetical
 * order while boxes are ticked, so nothing jumps under the pointer. Ad hoc permissions are not
 * part of creating a user: they are granted afterwards on the Permissions tab. The API assigns
 * every ticked role globally at creation. New users are always created active and sign in with
 * their email and the password.
 *
 * Client-side rules mirror the API validator (display name, email, at least one role and the
 * password are required; the email must be well formed; the password is not only whitespace)
 * and add a confirmation that must repeat the password. The password is held to the same rule
 * the sign-in applies and nothing more, and is sent exactly as typed. Every failed field,
 * whether it failed locally or on the server, shows its message directly beneath the field, and
 * the first failed control takes the focus. No message ever quotes the password, the
 * confirmation is never sent, and both password controls and the ticked roles are emptied when
 * the form is cancelled, succeeds or is destroyed.
 *
 * Every label, hint, button and client-side validation message reads through the vocabulary in
 * platform-access.terminology.ts (ADO #1542); messages the API reports and the catalog role
 * names, codes and descriptions are rendered as received.
 *
 * On success the created catalog row is emitted through `created`; the host leaves create mode,
 * reloads the users grid and opens the new user for editing.
 */
@Component({
  selector: "phms-platform-access-user-create-panel",
  imports: [NgTemplateOutlet, ReactiveFormsModule, TooltipModule, TerminologyIdentityDirective],
  templateUrl: "./platform-access-user-create-panel.component.html",
  styleUrl: "./platform-access-user-create-panel.component.scss",
})
export class PlatformAccessUserCreatePanelComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly platformAccessAdminService = inject(PlatformAccessAdminService);

  /**
   * The role catalog the Roles tab lists; every role in it can be ticked. An empty list means
   * the catalog has not loaded (or offers nothing), and the grid says so.
   */
  readonly roles = input<readonly PlatformRoleCatalogItem[]>([]);

  /** Emits the catalog row of the user the API created. */
  readonly created = output<PlatformUserCreated>();

  /** Emits when the person leaves the form without creating a user. */
  readonly cancelled = output<void>();

  readonly usage = PLATFORM_ACCESS_TERMINOLOGY.userCreatePanel;
  readonly text = createTerminologyUsageSignals(this.usage);
  readonly detailFields = computed(() => DETAIL_FIELDS.map((field) => this.describeField(field)));
  readonly passwordFields = computed(() => PASSWORD_FIELDS.map((field) => this.describeField(field)));
  readonly roleFilter = signal("");
  /** The catalog by role name: the order the grid lists the tiles in and the order the codes are sent in. */
  readonly sortedRoles = computed(() =>
    [...this.roles()].sort((left, right) => left.name.localeCompare(right.name)),
  );
  readonly filteredRoles = computed(() => {
    const filter = normalizeSearch(this.roleFilter());
    return this.sortedRoles().filter((role) => matchesRoleFilter(role, filter));
  });
  /** The codes ticked so far; kept equal to the roleCodes control so the tiles and the count follow it. */
  readonly selectedRoleCodes = signal<readonly string[]>([]);
  readonly selectedRoleCount = computed(() => this.selectedRoleCodes().length);
  readonly isSaving = signal(false);
  readonly submitAttempted = signal(false);
  readonly summaryError = signal<PlatformAccessAdminDisplayError | null>(null);

  readonly roleGroupId = ROLE_GROUP_ID;
  readonly roleLegendId = ROLE_LEGEND_ID;
  readonly roleHintId = ROLE_HINT_ID;
  readonly roleErrorId = ROLE_ERROR_ID;
  readonly roleFilterId = ROLE_FILTER_ID;

  readonly form = this.formBuilder.group(
    {
      displayName: ["", [requiredText, Validators.maxLength(USER_CREATE_MAX_LENGTHS.displayName)]],
      email: ["", [requiredText, Validators.email, Validators.maxLength(USER_CREATE_MAX_LENGTHS.email)]],
      phoneNumber: ["", [Validators.maxLength(USER_CREATE_MAX_LENGTHS.phoneNumber)]],
      jobTitle: ["", [Validators.maxLength(USER_CREATE_MAX_LENGTHS.jobTitle)]],
      department: ["", [Validators.maxLength(USER_CREATE_MAX_LENGTHS.department)]],
      roleCodes: this.formBuilder.control<string[]>([], [atLeastOneRole]),
      password: ["", [Validators.required, notOnlyWhitespace]],
      confirmPassword: ["", [Validators.required]],
    },
    { validators: [passwordsMatch] },
  );

  constructor() {
    this.destroyRef.onDestroy(() => this.clearSecretsAndRoles());

    // A role ticked before the catalog arrived is released when the catalog turns out not to offer it.
    effect(() => {
      const catalog = this.roles();
      if (catalog.length === 0) {
        return;
      }

      const offered = new Set(catalog.map((role) => role.code));
      const current = this.form.controls.roleCodes.value;
      const kept = current.filter((code) => offered.has(code));
      if (kept.length !== current.length) {
        this.setRoleCodes(kept);
      }
    });

    afterNextRender(() => this.focusField(FIELD_ORDER[0]));
  }

  controlFor(field: PlatformUserCreateFieldDefinition): FormControl<string> {
    return this.form.controls[field.name];
  }

  /** The API's length limit for a detail field; null for the password fields, which have none. */
  maxLengthFor(field: PlatformUserCreateFieldDefinition): number | null {
    return field.name === "password" || field.name === "confirmPassword"
      ? null
      : USER_CREATE_MAX_LENGTHS[field.name];
  }

  /**
   * The message to show beneath a text field, or null while the field is valid or untouched.
   * A message the API reported for the field wins over the local validator messages.
   */
  fieldMessage(field: PlatformUserCreateFieldDefinition): string | null {
    const control = this.form.controls[field.name];
    if (control.valid || !(control.touched || this.submitAttempted())) {
      return null;
    }

    const serverMessage = readServerMessage(control);
    if (serverMessage) {
      return serverMessage;
    }

    if (control.hasError("required")) {
      return `${field.label} ${this.text.validationRequiredSuffix()}`;
    }

    if (control.hasError("blank")) {
      return `${field.label} ${this.text.validationBlankSuffix()}`;
    }

    if (control.hasError("email")) {
      return this.text.validationEmail();
    }

    if (control.hasError("maxlength")) {
      const limit = control.getError("maxlength")?.requiredLength;
      return `${field.label} ${this.text.validationMaxLengthPrefix()} ${limit} ${this.text.validationMaxLengthSuffix()}`;
    }

    if (control.hasError("passwordMismatch")) {
      return this.text.validationPasswordMismatch();
    }

    return `${field.label} ${this.text.validationInvalidSuffix()}`;
  }

  describedBy(field: PlatformUserCreateFieldDefinition): string | null {
    const ids = [
      field.hints.length > 0 ? `user-create-hint-${field.name}` : null,
      this.fieldMessage(field) ? `user-create-error-${field.name}` : null,
    ].filter((id): id is string => id !== null);

    return ids.length > 0 ? ids.join(" ") : null;
  }

  onRoleFilter(event: Event): void {
    this.roleFilter.set((event.target as HTMLInputElement).value);
  }

  /**
   * The message to show beneath the roles, or null while at least one role is ticked or the
   * group is untouched. A message the API reported for the roles wins over the local one.
   */
  roleMessage(): string | null {
    const control = this.form.controls.roleCodes;
    if (control.valid || !(control.touched || this.submitAttempted())) {
      return null;
    }

    return readServerMessage(control) ?? this.text.roleRequired();
  }

  roleGroupDescribedBy(): string {
    return this.roleMessage() ? `${ROLE_HINT_ID} ${ROLE_ERROR_ID}` : ROLE_HINT_ID;
  }

  isRoleChosen(role: PlatformRoleCatalogItem): boolean {
    return this.selectedRoleCodes().includes(role.code);
  }

  /** Ticks or unticks one role. The other ticked roles are untouched. */
  toggleRole(role: PlatformRoleCatalogItem, checked: boolean): void {
    const current = this.form.controls.roleCodes.value;
    const next = checked
      ? (current.includes(role.code) ? current : [...current, role.code])
      : current.filter((code) => code !== role.code);
    this.setRoleCodes(next);

    const control = this.form.controls.roleCodes;
    control.markAsDirty();
    control.markAsTouched();
  }

  /** The tooltip of a role's info button: its code and its description, as the Roles tab shows them. */
  roleTooltip(role: PlatformRoleCatalogItem): string {
    return `${role.code} — ${role.description || this.text.noDescription()}`;
  }

  /** The accessible name of a role's info button. */
  roleDetailsLabel(role: PlatformRoleCatalogItem): string {
    return `${this.text.detailsFor()} ${role.name}`;
  }

  /** The usage behind the empty grid: no role in the catalog at all, or none left by the filter. */
  emptyRolesUsage(): TerminologyUsageDeclaration {
    return this.roles().length === 0 ? this.usage.roleNoneAvailable : this.usage.rolesEmpty;
  }

  emptyRolesText(): string {
    return this.roles().length === 0 ? this.text.roleNoneAvailable() : this.text.rolesEmpty();
  }

  submit(): void {
    if (this.isSaving()) {
      return;
    }

    this.submitAttempted.set(true);
    this.summaryError.set(null);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.focusFirstInvalidField();
      return;
    }

    this.isSaving.set(true);
    this.platformAccessAdminService.createUser(this.buildRequest())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.isSaving.set(false);
          this.clearSecretsAndRoles();
          this.created.emit(user);
        },
        error: (error: unknown) => {
          this.isSaving.set(false);
          this.applyServerError(toDisplayError(error));
        },
      });
  }

  cancel(): void {
    if (this.isSaving()) {
      return;
    }

    this.clearSecretsAndRoles();
    this.cancelled.emit();
  }

  /** Resolves a field's label and hints through the vocabulary so the template renders current wording. */
  private describeField(spec: PlatformUserCreateFieldSpec): PlatformUserCreateFieldDefinition {
    return {
      ...spec,
      label: this.text[spec.labelKey](),
      labelUsage: this.usage[spec.labelKey],
      hints: (spec.hintKeys ?? []).map((key) => ({ text: this.text[key](), usage: this.usage[key] })),
    };
  }

  /**
   * The request body: the details trimmed, the ticked roles as the catalog role codes the API
   * assigns (in the order the grid lists them) and the password exactly as typed. The user is
   * created active; the confirmation stays local.
   */
  private buildRequest(): PlatformUserCreateRequest {
    const values = this.form.getRawValue();
    const selected = new Set(values.roleCodes);
    const listed = this.sortedRoles().map((role) => role.code).filter((code) => selected.has(code));
    const unlisted = values.roleCodes.filter((code) => !listed.includes(code));

    return {
      displayName: values.displayName.trim(),
      email: values.email.trim(),
      phoneNumber: optionalText(values.phoneNumber),
      jobTitle: optionalText(values.jobTitle),
      department: optionalText(values.department),
      isActive: true,
      password: values.password,
      roleCodes: [...listed, ...unlisted],
    };
  }

  /** Writes the ticked codes to the control and to the signal the template reads, keeping both equal. */
  private setRoleCodes(codes: readonly string[], options?: { emitEvent?: boolean }): void {
    this.form.controls.roleCodes.setValue([...codes], options);
    this.selectedRoleCodes.set([...codes]);
  }

  /**
   * Empties both password controls, the ticked roles and the role filter so no secret outlives
   * the form and the next person created starts from no choice: on cancel, once the user is
   * created, and when the host destroys the component.
   */
  private clearSecretsAndRoles(): void {
    this.form.controls.password.reset("", { emitEvent: false });
    this.form.controls.confirmPassword.reset("", { emitEvent: false });
    this.form.controls.roleCodes.reset([], { emitEvent: false });
    this.selectedRoleCodes.set([]);
    this.roleFilter.set("");
    this.submitAttempted.set(false);
  }

  /**
   * Places each field-keyed server message on its control (a duplicate email arrives under
   * email, a rejected password under password, a missing, blank, repeated or unavailable role
   * under roleCodes). Messages that name no field of this form stay in the summary above the
   * fields.
   */
  private applyServerError(error: PlatformAccessAdminDisplayError): void {
    const fieldMessages = new Set<string>();

    for (const [key, messages] of Object.entries(error.fieldMessages ?? {})) {
      const control = this.resolveServerField(key);
      if (!control) {
        continue;
      }

      control.setErrors({ ...(control.errors ?? {}), server: messages.join(" ") });
      control.markAsTouched();
      messages.forEach((message) => fieldMessages.add(message));
    }

    const remainingMessages = error.messages.filter((message) => !fieldMessages.has(message));
    if (fieldMessages.size === 0) {
      this.summaryError.set(error);
      this.revealSummary();
    } else if (remainingMessages.length > 0) {
      this.summaryError.set({ ...error, messages: remainingMessages });
    }

    this.focusFirstInvalidField();
  }

  /**
   * The summary sits at the top of the form while Save stays pinned in the panel header, so a
   * problem that names no field is scrolled into view once it has rendered; otherwise someone
   * who saved from further down the form would see nothing happen.
   */
  private revealSummary(): void {
    afterNextRender(
      () => {
        const summary = this.host.nativeElement.querySelector<HTMLElement>('[data-testid="user-create-error-summary"]');
        summary?.scrollIntoView?.({ block: "nearest" });
      },
      { injector: this.injector },
    );
  }

  /** Maps a server field key (also an indexed one such as roleCodes[1]) to the control it is about. */
  private resolveServerField(key: string): AbstractControl | null {
    const normalizedKey = key
      .replace(/^\$\.?/, "")
      .replace(/^request\./i, "")
      .replace(/\[\d+\]$/, "")
      .toLowerCase();
    const name = FIELD_ORDER.find((candidate) => candidate !== "confirmPassword" && candidate.toLowerCase() === normalizedKey);

    return name ? this.form.controls[name] : null;
  }

  private focusFirstInvalidField(): void {
    const name = FIELD_ORDER.find((candidate) => this.form.controls[candidate].invalid);
    if (name) {
      this.focusField(name);
    }
  }

  /** Focuses a text field, or for the roles their first tile (the filter box when the grid is empty). */
  private focusField(name: UserCreateFieldName): void {
    const element = this.host.nativeElement;
    const target = name === "roleCodes"
      ? element.querySelector<HTMLElement>(`#${ROLE_GROUP_ID} input[type="checkbox"]`)
        ?? element.querySelector<HTMLElement>(`#${ROLE_FILTER_ID}`)
      : element.querySelector<HTMLElement>(`#user-create-${name}`);
    target?.focus();
  }
}

function readServerMessage(control: AbstractControl): string | null {
  const serverMessage: unknown = control.errors?.["server"];
  return typeof serverMessage === "string" && serverMessage.length > 0 ? serverMessage : null;
}

function requiredText(control: AbstractControl<string>): ValidationErrors | null {
  return typeof control.value === "string" && control.value.trim().length > 0 ? null : { required: true };
}

/** Flags a value made only of whitespace; an empty value is left to the required rule. */
function notOnlyWhitespace(control: AbstractControl<string>): ValidationErrors | null {
  const value = typeof control.value === "string" ? control.value : "";
  return value.length > 0 && value.trim().length === 0 ? { blank: true } : null;
}

/** The roles control must hold at least one code. */
function atLeastOneRole(control: AbstractControl<string[]>): ValidationErrors | null {
  return Array.isArray(control.value) && control.value.length > 0 ? null : { required: true };
}

/**
 * Group-level rule that reports on the confirmation control: once something is typed there it
 * must equal the password. Keeping the error on that control lets it render, focus and flag
 * aria-invalid exactly like every other field rule; the control's other errors are preserved.
 */
function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const password = group.get("password");
  const confirmation = group.get("confirmPassword");
  if (!password || !confirmation) {
    return null;
  }

  const repeated = typeof confirmation.value === "string" ? confirmation.value : "";
  const mismatch = repeated.length > 0 && repeated !== password.value;
  const errors: ValidationErrors = { ...(confirmation.errors ?? {}) };
  delete errors["passwordMismatch"];
  if (mismatch) {
    errors["passwordMismatch"] = true;
  }

  confirmation.setErrors(Object.keys(errors).length > 0 ? errors : null, { emitEvent: false });
  return mismatch ? { passwordMismatch: true } : null;
}

/** The same match the Roles tab applies: name, code or description contains the filter, ignoring case. */
function matchesRoleFilter(role: PlatformRoleCatalogItem, filter: string): boolean {
  if (!filter) {
    return true;
  }

  return [role.name, role.code, role.description]
    .some((value) => normalizeSearch(value).includes(filter));
}

function normalizeSearch(value: string | null | undefined): string {
  return (value ?? "").trim().toLocaleLowerCase();
}

function optionalText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function toDisplayError(error: unknown): PlatformAccessAdminDisplayError {
  if (error && typeof error === "object" && Array.isArray((error as PlatformAccessAdminDisplayError).messages)) {
    return error as PlatformAccessAdminDisplayError;
  }

  return mapPlatformAccessAdminError(error);
}
